import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Alert, CircularProgress } from '@mui/material';
import api from '../utils/api';
import { SplitAuthLayout, AmberButton, AuthInput } from '../components/design';

const MONO = "'IBM Plex Mono', monospace";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [tokenValid, setTokenValid] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) { setTokenValid(false); setVerifying(false); return; }
      try {
        const res = await api.get(`/api/auth/verify-reset-token/${token}`);
        setTokenValid(res.data.valid);
      } catch {
        setTokenValid(false);
        setError('This password reset link is invalid or has expired.');
      } finally {
        setVerifying(false);
      }
    };
    verifyToken();
  }, [token]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return; }
    if (formData.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setError('');
    setLoading(true);
    try {
      await api.post('/api/auth/reset-password', { token, password: formData.password });
      setIsSubmitted(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <SplitAuthLayout statusLine="// verifying reset link…">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', color: '#aebfdd' }}>
          <CircularProgress size={20} sx={{ color: '#2dd4bf' }} />
          <Box sx={{ fontFamily: MONO, fontSize: '13px', color: '#8298bd' }}>Verifying your reset link…</Box>
        </Box>
      </SplitAuthLayout>
    );
  }

  if (tokenValid === false) {
    return (
      <SplitAuthLayout statusLine="// reset link invalid">
        <Box sx={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(244,113,106,.14)', border: '1px solid rgba(244,113,106,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '20px', fontSize: '22px' }}>
          ✕
        </Box>
        <Box component="h1" sx={{ m: 0, mb: '12px', fontSize: '24px', fontWeight: 700, color: '#f4f8ff', letterSpacing: '-.01em' }}>
          Link invalid or expired
        </Box>
        <Box sx={{ mb: '28px', fontSize: '14.5px', color: '#8fa3c6', lineHeight: 1.6 }}>
          {error || 'This password reset link is invalid or has expired. Please request a new one.'}
        </Box>
        <AmberButton to="/forgot-password" size="lg" fullWidth>Request new link</AmberButton>
      </SplitAuthLayout>
    );
  }

  return (
    <SplitAuthLayout statusLine="// reset link verified ✓">
      {isSubmitted ? (
        <>
          <Box component="h1" sx={{ m: 0, mb: '12px', fontSize: '24px', fontWeight: 700, color: '#f4f8ff', letterSpacing: '-.01em' }}>
            Password reset
          </Box>
          <Alert severity="success" sx={{ mb: '20px', background: 'rgba(68,208,123,.1)', color: '#7fe0a6', border: '1px solid rgba(68,208,123,.3)', '& .MuiAlert-icon': { color: '#44d07b' } }}>
            Your password has been reset successfully! Redirecting to login…
          </Alert>
        </>
      ) : (
        <>
          <Box component="h1" sx={{ m: 0, mb: '6px', fontSize: '24px', fontWeight: 700, color: '#f4f8ff', letterSpacing: '-.01em' }}>
            Reset password
          </Box>
          <Box sx={{ mb: '24px', fontSize: '14.5px', color: '#8fa3c6' }}>
            Enter and confirm your new password.
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: '20px', background: 'rgba(244,113,106,.1)', color: '#f4a39e', border: '1px solid rgba(244,113,106,.3)', '& .MuiAlert-icon': { color: '#f4716a' } }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <AuthInput label="New password" id="password" type="password" name="password" value={formData.password} onChange={handleChange} placeholder="••••••••••" autoComplete="new-password" autoFocus required disabled={loading} />
            <AuthInput label="Confirm new password" id="confirmPassword" type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} placeholder="••••••••••" required disabled={loading} />
            <Box sx={{ fontFamily: MONO, fontSize: '11px', color: '#5f7398' }}>min. 8 characters</Box>
            <AmberButton size="lg" fullWidth disabled={loading || !formData.password || !formData.confirmPassword} onClick={handleSubmit}>
              {loading ? <CircularProgress size={18} sx={{ color: '#241200' }} /> : 'Reset password'}
            </AmberButton>
          </Box>
        </>
      )}
    </SplitAuthLayout>
  );
};

export default ResetPassword;
